import { Injectable } from '@angular/core';
import { firstValueFrom, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AgentConfigService } from '../agent-config/agent-config.service';
import { AgentConfig } from '../../models';
import { PrivilegesService } from '../../../../services/privileges-service/privileges.service';
import { ConfigurationService } from '../../../../services/configuration/configuration.service';
import { LoggerService } from '../../../../services/logger-services/logger.service';
import { ChatbotService } from '../../../../services/chatbot-services/chatbot.service';
import { environment } from '../../../../environments/environment';

/** Fallback domain aliases when API fails (lowercase). */
const REGULATION_DOMAIN_ALIASES_FALLBACK = ['mpr', 'cos', 'fds', 'mdv', 'chp'];

/**
 * Agent Access Control Service
 * 
 * Manages agent access control by filtering agents based on:
 * - Configuration file enabled flag
 * - Feature flags from ConfigurationService
 * - User privileges validation
 * 
 * This service ensures only authorized and enabled agents are available to users.
 */
@Injectable({
    providedIn: 'root'
})
export class AgentAccessControlService {
    private userPrivileges: string[] = [];
    private agentPrivileges: string[] = [];
    /** User-specific domain phrases from GET_URF_ALL_DOMAINS (lowercased). Used for regulations_agent. */
    private userDomainAliases: string[] = [];

    constructor(
        private readonly agentConfigService: AgentConfigService,
        private readonly configurationService: ConfigurationService,
        private readonly privilegesService: PrivilegesService,
        private readonly chatbotService: ChatbotService,
        private readonly logger: LoggerService
    ) {}

    /**
     * Get list of available agents filtered by access control rules
     * 
     * Filters agents based on:
     * 1. Agent config enabled flag
     * 2. Feature flags
     * 3. User privileges
     * 
     * @param userId - User ID to load privileges for (optional - will use environment.LoginUserID if not provided)
     * @param URFModuleId - Module ID filter (optional)
     * @param domainId - Domain ID filter (optional)
     * @param privilegesData - Optional pre-loaded privileges data to avoid API call
     * @returns Promise<AgentConfig[]> - Array of available agent configurations
     */
    async getAvailableAgents(
        userId?: any, 
        URFModuleId: number = 0, 
        domainId: number = 0,
        privilegesData?: { cookiesArray: string[]; agentPrivileges: string[] }
    ): Promise<AgentConfig[]> {
        try {
            await this.agentConfigService.loadAgentConfig();
            const allAgents = this.agentConfigService.getAllAgents();
            
            // Get user privileges - use provided userId or get from environment
            const userIdToUse = userId || environment.LoginUserID;
            
            if (userIdToUse) {
                await this.loadUserPrivileges(userIdToUse, URFModuleId, domainId, privilegesData);
            } else {
                await this.loadUserPrivileges('fallback_user', URFModuleId, domainId, privilegesData);
            }
            await this.loadUserDomains();

            const filtered: AgentConfig[] = [];
            const deniedAgents: Array<{agent: string, reason: string}> = [];
            
            for (const agent of allAgents) {
                if (await this.isAgentAvailable(agent)) {
                    filtered.push(agent);
                } else {
                    const accessCheck = await this.checkAgentAccess(agent.id);
                    deniedAgents.push({ agent: agent.id, reason: accessCheck.reason });
                }
            }
            
            this.logger.logInfo(`[AgentAccessControl] Available agents: ${filtered.length}/${allAgents.length}. Available: [${filtered.map(a => a.id).join(', ')}]`, 'getAvailableAgents');
            
            return filtered;
        } catch (error: any) {
            this.logger.logInfo('[AgentAccessControl] Error getting available agents', 'getAvailableAgents', error);
            return [];
        }
    }

    /**
     * Check if user can access a specific agent by ID
     * Public method for components to check access
     * 
     * @param agentId - Agent ID to check
     * @param userId - User ID (optional - will use environment.LoginUserID if not provided)
     * @param URFModuleId - Module ID filter (optional)
     * @param domainId - Domain ID filter (optional)
     * @param privilegesData - Optional pre-loaded privileges data to avoid API call
     * @returns Promise<boolean> - True if agent is accessible
     */
    async canAccessAgent(
        agentId: string, 
        userId?: any, 
        URFModuleId: number = 0, 
        domainId: number = 0,
        privilegesData?: { cookiesArray: string[]; agentPrivileges: string[] }
    ): Promise<boolean> {
        try {
            // Load privileges if not already loaded
            if (this.userPrivileges.length === 0) {
                const userIdToUse = userId || environment.LoginUserID;
                if (userIdToUse) {
                    await this.loadUserPrivileges(userIdToUse, URFModuleId, domainId, privilegesData);
                }
            }
            if (this.userDomainAliases.length === 0) {
                await this.loadUserDomains();
            }

            const accessCheck = await this.checkAgentAccess(agentId);
            return accessCheck.available;
        } catch (error: any) {
            this.logger.logInfo(`[AgentAccessControl] Error checking access for agent ${agentId}`, 'canAccessAgent', error);
            return false;
        }
    }

    /**
     * Check if a specific agent is available to the current user
     * 
     * Validates both feature flags AND user privileges
     * 
     * @param agent - Agent configuration to check
     * @returns Promise<boolean> - True if agent is available
     */
    private async isAgentAvailable(agent: AgentConfig): Promise<boolean> {
        try {
            // Step 1: Check if agent is enabled in configuration
            if (!agent.enabled) {
                this.logger.logInfo(`[AgentAccessControl] Agent ${agent.id} disabled in config`, 'isAgentAvailable');
                return false;
            }
            
            
            // Step 2: Check user privileges
            if (!this.hasRequiredPrivilege(agent.privilege_required)) {
                this.logger.logInfo(`[AgentAccessControl] User lacks privilege '${agent.privilege_required}' for agent ${agent.id}`, 'isAgentAvailable');
                return false;
            }

            // Step 3: For regulations_agent only, require domain-level regulation view (e.g. view_access_to_regulations_mpr)
            if (agent.id === "regulations_agent" && !this.hasDomainRegulationView()) {
                this.logger.logInfo(`[AgentAccessControl] User lacks domain regulation view for current domain (${environment.DomainName || ''}) for agent ${agent.id}`, 'isAgentAvailable');
                return false;
            }
            
            this.logger.logInfo(`[AgentAccessControl] Agent ${agent.id} is available to user`, 'isAgentAvailable');
            return true;
        } catch (error: any) {
            this.logger.logInfo('[AgentAccessControl] Error checking agent availability', 'isAgentAvailable', error);
            return false;
        }
    }

    /**
     * Load user privileges from PrivilegesService OR use provided privileges data
     * 
     * @param userId - User ID to get privileges for
     * @param URFModuleId - Module ID filter (optional)
     * @param domainId - Domain ID filter (optional)
     * @param privilegesData - Optional pre-loaded privileges data to avoid API call
     */
    private async loadUserPrivileges(
        userId: any, 
        URFModuleId: number = 0, 
        domainId: number = 0,
        privilegesData?: { cookiesArray: string[]; agentPrivileges: string[] }
    ): Promise<void> {
        try {
            // If privileges data is already provided, use it directly
            if (privilegesData && privilegesData.cookiesArray && privilegesData.agentPrivileges) {
                this.userPrivileges = privilegesData.cookiesArray;
                this.agentPrivileges = privilegesData.agentPrivileges;
                
                this.logger.logInfo(`[AgentAccessControl] Using provided privileges data: ${this.userPrivileges.length} user privileges and ${this.agentPrivileges.length} agent privileges`, 'loadUserPrivileges');
                return;
            }
            
            // Otherwise, fetch from API
            const fetchedPrivilegesData = await this.privilegesService.getPrivilegesList(userId, URFModuleId, domainId);
            this.userPrivileges = fetchedPrivilegesData.cookiesArray;
            this.agentPrivileges = fetchedPrivilegesData.agentPrivileges;
            
            this.logger.logInfo(`[AgentAccessControl] Fetched ${this.userPrivileges.length} user privileges and ${this.agentPrivileges.length} agent privileges from API`, 'loadUserPrivileges');
        } catch (error: any) {
            this.logger.logInfo('[AgentAccessControl] Error loading user privileges', 'loadUserPrivileges', error);
            this.userPrivileges = [];
            this.agentPrivileges = [];
        }
    }

    /**
     * Load user-specific domains from GET_URF_ALL_DOMAINS (/common-uam/tm/domains).
     * Response shape: { code: 200, data: [ { key, value, domainPhrase } ] }.
     * Populates userDomainAliases (lowercased domainPhrase) for hasDomainRegulationView.
     */
    private async loadUserDomains(): Promise<void> {
        try {
            const res = await firstValueFrom(
                this.chatbotService.GetURFAllDomains().pipe(
                    catchError(() => of({ code: 0, data: [] }))
                )
            );
            const data = res?.data;
            if (res?.code === 200 && Array.isArray(data)) {
                this.userDomainAliases = data
                    .map((d: { domainPhrase?: string }) => (d?.domainPhrase ?? '').toString().toLowerCase().trim())
                    .filter(Boolean);
                this.logger.logInfo(`[AgentAccessControl] Loaded ${this.userDomainAliases.length} user domains from API`, 'loadUserDomains');
            } else {
                this.userDomainAliases = [...REGULATION_DOMAIN_ALIASES_FALLBACK];
                this.logger.logInfo('[AgentAccessControl] Using fallback domain list (API response invalid or failed)', 'loadUserDomains');
            }
        } catch (error: any) {
            this.logger.logInfo('[AgentAccessControl] Error loading user domains', 'loadUserDomains', error);
            this.userDomainAliases = [...REGULATION_DOMAIN_ALIASES_FALLBACK];
        }
    }

    /**
     * Get current user privileges
     * 
     * @returns string[] - Array of user privilege strings
     */
    getUserPrivileges(): string[] {
        return [...this.userPrivileges];
    }

    /**
     * Check if user has required privilege for an agent
     * 
     * @param requiredPrivilege - The privilege required by the agent
     * @returns boolean - True if user has the required privilege
     */
    private hasRequiredPrivilege(requiredPrivilege: string): boolean {
        if (!requiredPrivilege) {
            return true; // No privilege required
        }

        // EXACT MATCH ONLY - Check agent-specific privileges first
        const hasAgentPrivilege = this.agentPrivileges.some(privilege => privilege === requiredPrivilege);

        if (hasAgentPrivilege) {
            return true;
        }

        // Check in general user privileges as fallback - EXACT MATCH ONLY
        const hasUserPrivilege = this.userPrivileges.some(privilege => privilege === requiredPrivilege);
        
        return hasUserPrivilege;
    }

    /**
     * Check if user has the domain-level regulation view OR gri.regulation.read.
     * Used for regulations_agent: show when user has "Regulations Agent Access" AND
     * (view_access_to_regulations_<currentDomain> OR gri.regulation.read).
     * gri.regulation.read is not domain-specific; having it in any domain is enough.
     */
    private hasDomainRegulationView(): boolean {
        if (this.userPrivileges.some(p => p === 'gri.regulation.read')) {
            return true;
        }
        const domainAlias = (environment.DomainName || '').toLowerCase().trim();
        const allowed = this.userDomainAliases.length > 0 ? this.userDomainAliases : REGULATION_DOMAIN_ALIASES_FALLBACK;
        if (!domainAlias || !allowed.includes(domainAlias)) {
            return false;
        }
        const requiredPrivilege = `view_access_to_regulations_${domainAlias}`;
        return this.userPrivileges.some(p => p === requiredPrivilege);
    }

    /**
     * Get current agent privileges
     * 
     * @returns string[] - Array of agent-specific privilege strings
     */
    getAgentPrivileges(): string[] {
        return [...this.agentPrivileges];
    }

    /**
     * Filter privileges that contain "agent" in them
     * 
     * @param cookiesArray - Array of privilege strings to filter
     * @returns string[] - Filtered array containing only items with "agent"
     */
    filterAgentPrivileges(cookiesArray: string[]): string[] {
        try {
            const agentPrivileges = cookiesArray.filter(item => 
                item.toLowerCase().includes('agent')
            );
            
            this.logger.logInfo(`[AgentAccessControl] Filtered agent privileges: ${agentPrivileges.length} out of ${cookiesArray.length}`, 'filterAgentPrivileges');
            
            return agentPrivileges;
        } catch (error: any) {
            this.logger.logInfo('[AgentAccessControl] Error filtering agent privileges', 'filterAgentPrivileges', error);
            return [];
        }
    }

    /**
     * Check specific agent availability for debugging
     * 
     * @param agentId - Agent ID to check
     * @returns Promise<{available: boolean, reason: string}> - Availability status and reason
     */
    async checkAgentAccess(agentId: string): Promise<{available: boolean, reason: string}> {
        try {
            await this.agentConfigService.loadAgentConfig();
            const agents = this.agentConfigService.getAllAgents();
            const agent = agents.find(a => a.id === agentId);
            
            if (!agent) {
                return { available: false, reason: 'Agent not found' };
            }

            if (!agent.enabled) {
                return { available: false, reason: 'Agent disabled in configuration' };
            }

            const isFeatureEnabled = await this.configurationService.isAgentEnabled(agent.id);
            if (!isFeatureEnabled) {
                return { available: false, reason: 'Agent disabled by feature flag' };
            }

            if (!this.hasRequiredPrivilege(agent.privilege_required)) {
                return { 
                    available: false, 
                    reason: `Missing required privilege: ${agent.privilege_required}. User has: [${this.agentPrivileges.join(', ')}]` 
                };
            }

            if (agent.id === "regulations_agent" && !this.hasDomainRegulationView()) {
                const domainAlias = (environment.DomainName || '').toLowerCase();
                return {
                    available: false,
                    reason: `Missing regulation access: need view_access_to_regulations_${domainAlias} or gri.regulation.read. User privileges: [${this.userPrivileges.join(', ')}]`
                };
            }

            return { available: true, reason: 'All checks passed' };
        } catch (error: any) {
            return { available: false, reason: `Error: ${error.message}` };
        }
    }

    /**
     * Debug method to test privilege loading and environment setup
     */
    /**
     * Debug method to test privilege loading and agent access
     * Use this method for troubleshooting access issues
     */
    async debugPrivilegeSetup(userId?: any): Promise<void> {
        
        const userIdToUse = userId || environment.LoginUserID;
        
        if (userIdToUse) {
            await this.loadUserPrivileges(userIdToUse);
        }
        
        try {
            await this.agentConfigService.loadAgentConfig();
        } catch (error) {
            console.error(`Error loading agent config:`, error);
        }        
    }
}

